/**
 * ClientDocuments Component
 * Displays all project documents organized by category with viewer modal
 *
 * @component
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useClientProject } from "../../hooks/useProjects";
import "./ClientDocuments.css";
import { FaExclamationTriangle } from "react-icons/fa";

/**
 * ClientDocuments - Main documents page for client portal
 * Shows floor plans, site plans, client documents, and other project documents
 *
 * @returns {JSX.Element} The documents page component
 */
const ClientDocuments = () => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get project data for navbar
  const {
    data: projectResponse,
    isLoading: projectLoading,
    error: projectError,
  } = useClientProject();
  const project = projectResponse?.data || projectResponse;

  // Fetch documents
  const {
    data: documentsData,
    isLoading: docsLoading,
    error,
  } = useQuery({
    queryKey: ["projectDocuments", project?.id],
    queryFn: async () => {
      if (!project?.id) throw new Error("No project ID available");

      const functions = getFunctions();
      const getDocuments = httpsCallable(functions, "getProjectDocuments");
      const result = await getDocuments({ projectId: project.id });
      return result.data.data;
    },
    enabled: !!project?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  /**
   * Opens the document viewer modal
   * @param {Object} document - The document object to view
   */
  const openDocument = (doc) => {
    setSelectedDocument(doc);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden"; // now refers to DOM document
  };

  /**
   * Closes the document viewer modal
   */
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
    document.body.style.overflow = ""; // Restore scroll
  };

  /**
   * Downloads a file by fetching it and creating a blob URL
   * @param {string} url - The file URL
   * @param {string} filename - The filename to save as
   */
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback to opening in new tab
      window.open(url, "_blank");
    }
  };

  /**
   * Renders a single document card
   * @param {Object} doc - Document object
   * @returns {JSX.Element} Document card
   */
  const DocumentCard = ({ doc }) => {
    const isPDF = doc.type && doc.type.includes("pdf");
    const isImage = doc.type && doc.type.startsWith("image/");

    // Format file size
    const formatSize = (bytes) => {
      if (!bytes) return "Unknown size";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(1)} MB`;
    };

    return (
      <div
        className="card-secondary document-card"
        onClick={() => openDocument(doc)}
      >
        <div className="document-thumbnail">
          {isPDF && (
            <div className="document-icon pdf">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 2V8H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 13H8V17H10C10.5304 17 11.0391 16.7893 11.4142 16.4142C11.7893 16.0391 12 15.5304 12 15C12 14.4696 11.7893 13.9609 11.4142 13.5858C11.0391 13.2107 10.5304 13 10 13Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
          {isImage && doc.thumbnails?.large && (
            <img src={doc.thumbnails.large.url} alt={doc.filename} />
          )}
          {!isPDF && !isImage && (
            <div className="document-icon generic">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13 2V9H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>
        <div className="document-info">
          <h4 className="document-name">{doc.filename}</h4>
          <p className="document-size">{formatSize(doc.size)}</p>
        </div>
      </div>
    );
  };

  /**
   * Renders a category section with its documents
   * @param {string} title - Category title
   * @param {Array} documents - Array of document objects
   * @returns {JSX.Element} Category section
   */
  const DocumentCategory = ({ title, documents }) => {
    if (!documents || documents.length === 0) return null;

    return (
      <div className="card document-category">
        <h2 className="category-title">{title}</h2>
        <div className="documents-grid">
          {documents.map((doc) => (
            <DocumentCard key={doc.id} doc={doc} />
          ))}
        </div>
      </div>
    );
  };

  // Loading state
  if (projectLoading || docsLoading) {
    return (
      <>
        <div className="component-loading">
          <div className="component-spinner"></div>
          <p>Loading project documents...</p>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="dashboard-error">
          <FaExclamationTriangle />
          <h2>Unable to Load Project Documents</h2>
          <p>{projectError.message}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </>
    );
  }

  // No documents
  const hasDocuments =
    documentsData &&
    (documentsData.floorPlans?.length > 0 ||
      documentsData.sitePlans?.length > 0 ||
      documentsData.clientDocuments?.length > 0 ||
      documentsData.otherDocuments?.length > 0);

  return (
    <>
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Project Documents</h1>
          <p className="page-subtitle">Quickly Access Your Documents</p>
        </div>

        {!hasDocuments ? (
          <div className="card docs-empty-state documents-empty">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 2V9H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3>No Documents Yet</h3>
            <p>
              Your project documents will appear here once they're uploaded.
            </p>
          </div>
        ) : (
          <div>
            <DocumentCategory
              title="Floor Plans"
              documents={documentsData.floorPlans}
            />
            <DocumentCategory
              title="Site Plans"
              documents={documentsData.sitePlans}
            />
            <DocumentCategory
              title="Client Documents"
              documents={documentsData.clientDocuments}
            />
            <DocumentCategory
              title="Other Documents"
              documents={documentsData.otherDocuments}
            />
          </div>
        )}

        {/* Document Viewer Modal */}
        {isModalOpen && selectedDocument && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-container modal-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={closeModal}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="modal-header modal-header-with-close">
                <h3>{selectedDocument.filename}</h3>
                <button
                  className="btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(
                      selectedDocument.url,
                      selectedDocument.filename
                    );
                  }}
                >
                  Download
                </button>
              </div>
              <div className="modal-body">
                {selectedDocument.type?.includes("pdf") ? (
                  <iframe
                    src={selectedDocument.url}
                    title={selectedDocument.filename}
                    className="pdf-viewer"
                  />
                ) : selectedDocument.type?.startsWith("image/") ? (
                  <img
                    src={selectedDocument.url}
                    alt={selectedDocument.filename}
                    className="image-viewer"
                  />
                ) : (
                  <div className="unsupported-file">
                    <p>Preview not available for this file type.</p>
                    <button
                      className="btn-primary download-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(
                          selectedDocument.url,
                          selectedDocument.filename
                        );
                      }}
                    >
                      Download to view
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClientDocuments;
